import {
  getDetailedDriveStatisticsOptions,
  getRequirementSubintervalsOptions,
  getScheduleForIntervalOptions,
  getAllBillingPeriodsOptions,
  reorderScheduledDrivesMutation,
  rescheduleDriveMutation,
  getRequirementSubintervalsQueryKey,
  getAllActiveRoutesOptions,
} from '@/api/main'
import {
  VehicleResponse,
  RequirementSubintervalsResponse,
  DetailedDriveStatistics,
  DailyScheduleResponse,
} from '@/api/main/types.gen'
import { useSuspenseQueries, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useMemo, useCallback } from 'react'
import {
  partitionIntervalsByTime,
  splitDrivesByDay,
  getOverlappingBillingPeriods,
  findBillingPeriodForTime,
  aggregateRequirementSubintervals,
} from '@/utils/scheduling'
import { Box } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { PastDrivesSection } from './PastDrivesSection'
import { RestDrivesList } from './RestDrivesList'
import { DragDropProvider } from './dragAndDrop/DragDropContext'
import { DragItem } from './dragAndDrop/useCustomDragDrop'
import { ScheduledDriveInterval } from '@/utils/scheduling'
import { useDriveCalculations } from './hooks/useDriveCalculations'
import {
  getIntervalByDroppableId,
  calculateScheduleAfterDrive,
  calculateScheduleBeforeDrive,
} from './dragAndDrop/dragHandlerHelpers'
import { useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { move, head } from 'ramda'
import { queryClient } from '@/lib/queryClient'
import {
  getDefaultSchedulingBounds,
  isWithinDefaultSchedulingBounds,
  clampToDefaultSchedulingBounds,
} from '@/utils/schedulingBounds'

dayjs.extend(utc)
dayjs.extend(timezone)

const Scheduler = ({
  vehicles,
  intervalStart,
  intervalEnd,
}: {
  vehicles: Array<VehicleResponse>
  intervalStart: string
  intervalEnd: string
}) => {
  const navigate = useNavigate()

  const { data: billingPeriods } = useSuspenseQuery(getAllBillingPeriodsOptions())
  const { data: routes } = useSuspenseQuery(getAllActiveRoutesOptions({}))

  // Find overlapping billing periods
  const overlappingPeriods = useMemo(
    () => getOverlappingBillingPeriods(intervalStart, intervalEnd, billingPeriods),
    [intervalStart, intervalEnd, billingPeriods]
  )

  // Create queries for requirementSubintervals for each overlapping period
  const requirementSubintervalsQueries = useMemo(
    () =>
      overlappingPeriods.length > 0
        ? overlappingPeriods.map(period =>
            getRequirementSubintervalsOptions({
              path: {
                billing_period_id: period.id,
                starting_from: intervalStart,
                ending_at: intervalEnd,
              },
            })
          )
        : [],
    [overlappingPeriods, intervalStart, intervalEnd]
  )

  // Fetch all data: requirementSubintervals (for all periods), driveStatistics, scheduleForInterval
  const allQueriesResult = useSuspenseQueries({
    queries: [
      ...requirementSubintervalsQueries,
      getDetailedDriveStatisticsOptions({
        path: {
          timestamp_from: intervalStart,
          timestamp_to: intervalEnd,
        },
      }),
      getScheduleForIntervalOptions({
        path: {
          interval_start: intervalStart,
          interval_end: intervalEnd,
        },
      }),
    ],
    combine: results => {
      // Handle case when there are no overlapping periods
      const numRequirementQueries = requirementSubintervalsQueries.length
      const requirementSubintervalsResults =
        numRequirementQueries > 0
          ? results.slice(0, numRequirementQueries).map(r => r.data as RequirementSubintervalsResponse)
          : []
      const driveStatistics = results[numRequirementQueries].data as DetailedDriveStatistics[]
      const scheduleForInterval = results[numRequirementQueries + 1].data as DailyScheduleResponse

      return {
        requirementSubintervals: aggregateRequirementSubintervals(requirementSubintervalsResults),
        driveStatistics,
        scheduleForInterval,
      }
    },
  })

  const { requirementSubintervals, driveStatistics, scheduleForInterval } = allQueriesResult

  const scheduledDriveIntervals = useMemo(
    () =>
      Object.fromEntries(
        vehicles.map(({ id }) => [id, requirementSubintervals.drive_subintervals.filter(it => it.vehicle_id === id)])
      ),
    [vehicles, requirementSubintervals]
  )

  // Do not touch this
  const partitionedIntervals = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(scheduledDriveIntervals).map(([vehicleId, intervals]) => [
          vehicleId,
          partitionIntervalsByTime(intervals),
        ])
      ),
    [scheduledDriveIntervals]
  )

  useDriveCalculations(scheduledDriveIntervals, driveStatistics)

  // Create mutations that can work with dynamic billing periods
  const { mutate: reorderDrivesMutation } = useMutation({
    ...reorderScheduledDrivesMutation(),
    onSuccess: () => {
      showNotification({ message: 'Drives reordered' })
      // Invalidate all requirementSubintervals queries for overlapping periods
      overlappingPeriods.forEach(period => {
        const queryKey = getRequirementSubintervalsQueryKey({
          path: {
            billing_period_id: period.id,
            starting_from: intervalStart,
            ending_at: intervalEnd,
          },
        })
        queryClient.invalidateQueries({ queryKey })
      })
    },
    onError: () => {
      showNotification({ message: 'Error reordering drives', color: 'red' })
    },
  })

  const { mutate: rescheduleDriveMutate } = useMutation({
    ...rescheduleDriveMutation(),
    onSuccess: () => {
      showNotification({ message: 'Drive rescheduled' })
      // Invalidate all requirementSubintervals queries for overlapping periods
      overlappingPeriods.forEach(period => {
        const queryKey = getRequirementSubintervalsQueryKey({
          path: {
            billing_period_id: period.id,
            starting_from: intervalStart,
            ending_at: intervalEnd,
          },
        })
        queryClient.invalidateQueries({ queryKey })
      })
    },
    onError: () => {
      showNotification({ message: 'Error rescheduling drive', color: 'red' })
    },
  })

  const handleDragEnd = useCallback(
    (source: DragItem, destination: { droppableId: string; index: number } | null) => {
      if (!destination) {
        return
      }

      const sourceInterval = getIntervalByDroppableId(source.droppableId, partitionedIntervals)
      const destinationInterval = getIntervalByDroppableId(destination.droppableId, partitionedIntervals)

      if (!sourceInterval) {
        return
      }

      // Get the source drive
      const sourceDrive = sourceInterval.drives[source.index]
      if (!sourceDrive) {
        return
      }

      // Find which billing period the drive belongs to based on its start time
      // This is important at month boundaries where default bounds might span two periods
      const billingPeriodId = findBillingPeriodForTime(sourceDrive.start_after, overlappingPeriods)
      if (!billingPeriodId) {
        showNotification({ message: 'No billing period found for this drive', color: 'red' })
        return
      }

      // Handle dropping into empty column (no intervals for that vehicle)
      if (destination.droppableId.endsWith('#empty')) {
        const [vehicleId] = destination.droppableId.split('#', 2)

        // Get default scheduling bounds for the drive's date
        const driveDate = dayjs.utc(sourceDrive.start_after).startOf('day').toDate()
        const defaultBounds = getDefaultSchedulingBounds(driveDate)

        // Clamp the drive's times to the default bounds
        const clampedStart = clampToDefaultSchedulingBounds(sourceDrive.start_after, driveDate)
        const clampedEnd = clampToDefaultSchedulingBounds(sourceDrive.finish_before, driveDate)

        // Validate that the drive can fit within the default bounds
        if (
          !isWithinDefaultSchedulingBounds(clampedStart, driveDate) ||
          !isWithinDefaultSchedulingBounds(clampedEnd, driveDate)
        ) {
          showNotification({
            message: 'Drive cannot be moved outside the default scheduling window (06:00-05:00 next day)',
            color: 'red',
          })
          return
        }

        // Create a new interval with the default bounds
        rescheduleDriveMutate(
          {
            body: {
              scheduled_drive_id: sourceDrive.id,
              destination_vehicle_id: vehicleId,
              interval_start: defaultBounds.start,
              interval_end: defaultBounds.end,
            },
          },
          {
            onSuccess: () => {
              showNotification({ message: 'Drive rescheduled successfully', color: 'green' })
            },
            onError: () => {
              showNotification({ message: 'Failed to reschedule drive', color: 'red' })
            },
          }
        )
        return
      }

      if (!destinationInterval) {
        return
      }

      // Validate that destination interval is within default scheduling bounds
      const destinationIntervalDate = dayjs.utc(destinationInterval.interval.starting_from).startOf('day').toDate()
      if (
        !isWithinDefaultSchedulingBounds(destinationInterval.interval.starting_from, destinationIntervalDate) ||
        !isWithinDefaultSchedulingBounds(destinationInterval.interval.ending_at, destinationIntervalDate)
      ) {
        showNotification({
          message: 'Cannot move drive outside the default scheduling window (06:00-05:00 next day)',
          color: 'red',
        })
        return
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return
      }

      if (destination.droppableId === source.droppableId && destination.index !== source.index) {
        // Reordering within the same interval
        const newList = move(source.index, destination.index, [...sourceInterval.drives])
        const firstDrive = head(newList)
        if (firstDrive) {
          reorderDrivesMutation({
            body: {
              drive_ids: newList.map(drive => drive.id),
              interval_start: firstDrive.start_after,
              interval_end: firstDrive.finish_before,
              vehicle_id: sourceInterval.vehicle_id,
            },
          })
        }
      } else {
        // Rescheduling between intervals
        const scheduleAfterDrive = calculateScheduleAfterDrive(destination.index, destinationInterval.drives)
        const scheduleBeforeDrive = calculateScheduleBeforeDrive(destination.index, destinationInterval.drives)

        const rescheduleBody: {
          interval_start: string
          interval_end: string
          destination_vehicle_id: string
          scheduled_drive_id: number
          schedule_after_drive?: number
          schedule_before_drive?: number
        } = {
          interval_start: destinationInterval.interval.starting_from,
          interval_end: destinationInterval.interval.ending_at,
          destination_vehicle_id: destinationInterval.vehicle_id,
          scheduled_drive_id: sourceDrive.id,
        }

        // Use schedule_before_drive when inserting at position 0
        if (scheduleBeforeDrive !== undefined) {
          rescheduleBody.schedule_before_drive = scheduleBeforeDrive
        } else if (scheduleAfterDrive !== undefined) {
          rescheduleBody.schedule_after_drive = scheduleAfterDrive
        }

        rescheduleDriveMutate({
          body: rescheduleBody,
        })
      }
    },
    [partitionedIntervals, reorderDrivesMutation, rescheduleDriveMutate, overlappingPeriods]
  )

  const handleOpenDriveModal = useCallback(
    (drive: ScheduledDriveInterval['drives'][0]) => {
      navigate({
        to: '/scheduling/$startDate/$endDate/drive/$action/$id',
        params: {
          startDate: encodeURIComponent(intervalStart),
          endDate: encodeURIComponent(intervalEnd),
          action: 'edit',
          id: drive.id.toString(),
        },
      })
    },
    [navigate, intervalStart, intervalEnd]
  )

  const dayDrivesData = useMemo(
    () => splitDrivesByDay(driveStatistics, intervalStart, intervalEnd),
    [driveStatistics, intervalStart, intervalEnd]
  )

  return (
    <DragDropProvider onDragEnd={handleDragEnd}>
      <Box
        style={{ display: 'grid', gridTemplateColumns: `repeat(${vehicles.length}, minmax(200px, 1fr))`, gap: '1rem' }}
      >
        <PastDrivesSection dayDrivesData={dayDrivesData} vehicles={vehicles} routes={routes} />
        <RestDrivesList
          partitionedIntervals={partitionedIntervals}
          vehicles={vehicles}
          onDriveClick={handleOpenDriveModal}
          scheduleForInterval={scheduleForInterval}
          routes={routes}
        />
      </Box>
    </DragDropProvider>
  )
}

export default Scheduler
